import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageMeta from "@/components/feature/PageMeta";
import PosterGrid from "@/components/base/PosterGrid";
import Pagination from "@/components/base/Pagination";
import SectionHeader from "@/components/base/SectionHeader";
import FilterBar from "@/components/feature/FilterBar";
import {
  ITEMS_PER_PAGE,
  defaultFilter,
  getHot,
  queryTitles,
  type FilterState,
  type SortKey,
} from "@/utils/catalog";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = (searchParams.get("q") || "").trim();

  const [input, setInput] = useState(keyword);
  const [filter, setFilter] = useState<FilterState>(defaultFilter);
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setInput(keyword);
    setFilter(defaultFilter);
    setSort("latest");
    setPage(1);
  }, [keyword]);

  const results = useMemo(
    () => (keyword ? queryTitles({ keyword, filter, sort }) : []),
    [keyword, filter, sort],
  );

  const totalPages = Math.max(1, Math.ceil(results.length / ITEMS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = results.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const hotPicks = getHot(6);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const word = input.trim();
    setSearchParams(word ? { q: word } : {});
    setPage(1);
  };

  return (
    <Layout>
      <PageMeta
        title={keyword ? `搜索“${keyword}” - 中文字幕在线免费看` : "影视搜索 - 中文字幕在线免费看"}
        description={
          keyword
            ? `为你找到与“${keyword}”相关的中文字幕电影与电视剧，可按地区、年份、题材进一步筛选，快速进入详情页在线观看。`
            : "输入片名、导演或演员，快速检索站内中文字幕电影与电视剧资源。"
        }
        keywords={`${keyword || "影视搜索"},中文字幕,在线观看`}
      />

      <div className="border-b border-background-200 bg-background-100 px-4 py-7 md:px-8 md:py-9">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="cursor-pointer transition-colors hover:text-primary-700">首页</Link>
          <i className="ri-arrow-right-s-line text-sm" aria-hidden="true"></i>
          <span className="text-foreground-800">搜索结果</span>
        </nav>

        <h1 className="mt-3 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
          搜索结果
          {keyword ? <span className="ml-2 text-base font-normal text-accent-600">“{keyword}”</span> : null}
        </h1>
        <p className="mt-2 text-xs text-foreground-600 md:text-sm">
          {keyword
            ? `共匹配到 ${results.length} 部相关作品，可继续按地区、年份与题材缩小范围。`
            : "支持片名、导演、演员与剧情关键词的模糊匹配，输入后回车即可检索。"}
        </p>

        <form onSubmit={handleSubmit} className="mt-5 flex w-full max-w-[560px] items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 focus-within:border-primary-400">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center">
            <i className="ri-search-line text-base text-foreground-400" aria-hidden="true"></i>
          </span>
          <input
            type="search"
            name="q"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="例如：悬疑、长夜将明、段奕辰"
            aria-label="搜索影视作品"
            className="w-full min-w-0 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
          />
          <button
            type="submit"
            className="shrink-0 cursor-pointer whitespace-nowrap rounded bg-primary-600 px-4 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-700"
          >
            搜索
          </button>
        </form>
      </div>

      <div className="px-4 py-6 md:px-8 md:py-8">
        <FilterBar
          filter={filter}
          sort={sort}
          onFilterChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
          onSortChange={(next) => {
            setSort(next);
            setPage(1);
          }}
          onReset={() => {
            setFilter(defaultFilter);
            setSort("latest");
            setPage(1);
          }}
        />

        {pageItems.length > 0 ? (
          <>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-2 text-xs text-foreground-600">
              <p>
                共找到 <span className="font-medium text-foreground-950">{results.length}</span> 部作品
              </p>
              <p className="text-foreground-500">每页展示 {ITEMS_PER_PAGE} 部</p>
            </div>
            <div className="mt-4">
              <PosterGrid items={pageItems} />
            </div>
            <Pagination page={currentPage} totalPages={totalPages} total={results.length} onChange={setPage} />
          </>
        ) : (
          <div className="mt-6">
            <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-14 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
                <i className="ri-search-line text-2xl text-primary-500" aria-hidden="true"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                {keyword ? "没有找到相关影视作品" : "输入关键词开始搜索"}
              </h2>
              <p className="mt-2 max-w-[460px] text-xs leading-6 text-foreground-600">
                {keyword
                  ? "试试更短的片名关键词，或换个题材词，例如「悬疑」「古装」「战争」。也可以从下方热门作品中直接挑选。"
                  : "站内收录中文字幕电影与电视剧，可按片名、导演、演员或剧情关键词检索。"}
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-2.5">
                <Link
                  to="/category/movie"
                  className="cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
                >
                  浏览全部电影
                </Link>
                <Link
                  to="/category/tv"
                  className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  浏览全部电视剧
                </Link>
              </div>
            </div>

            <div className="mt-10">
              <SectionHeader title="热门影视推荐" subtitle="站内播放热度最高的作品，先从这里挑一部" />
              <PosterGrid items={hotPicks} />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}